import type { ExpectedRevitDocumentIdentity } from '../../../api/revit/revitBridge';
import { type ActiveDocumentInfo } from '../../../hooks/ActiveDocumentProvider';
import { type ParameterBindingKind } from '../../revit/projectParameterMutationPlan';
import { type ParameterAdditionResult } from './models';

export function normalizeProjectIdentityPart(
  value: string | null | undefined,
): string | null {
  const normalized = value?.trim().replace(/\//g, '\\');
  return normalized ? normalized : null;
}

export function formatConnectorTargetLabel(
  destination: 'project' | 'family',
  bindingKind: ParameterBindingKind,
): string {
  return `${destination === 'project' ? 'Project' : 'Family'} ${bindingKind}`;
}

export function describeConnectorExecutionMode(
  destination: 'project' | 'family',
  bindingKind: ParameterBindingKind,
): string {
  if (destination === 'family') {
    const projectLabel = formatConnectorTargetLabel('project', bindingKind);
    const familyLabel = formatConnectorTargetLabel(destination, bindingKind).toLocaleLowerCase();
    return `${projectLabel} for pipes and ${
      familyLabel
    } for editable family categories`;
  }

  return `${formatConnectorTargetLabel(destination, bindingKind)} for all connector categories`;
}

export function resolveProjectUndoKey(
  activeDocumentInfo: Pick<ActiveDocumentInfo, 'projectKey'> | null,
  settings: { projectPath?: string; revitProjectName: string } | null,
): string | null {
  if (activeDocumentInfo?.projectKey) {
    return activeDocumentInfo.projectKey;
  }

  if (!settings) {
    return null;
  }

  const projectPath = normalizeProjectIdentityPart(settings.projectPath);
  if (projectPath) {
    return projectPath.toLowerCase();
  }

  const projectName = normalizeProjectIdentityPart(settings.revitProjectName);
  return projectName ? `name:${projectName.toLowerCase()}` : null;
}

export function buildExpectedDocumentIdentity(
  activeDocumentInfo: Pick<ActiveDocumentInfo, 'documentName' | 'documentPath'>,
): ExpectedRevitDocumentIdentity | undefined {
  const expectedDocumentName = activeDocumentInfo.documentName?.trim();
  const expectedDocumentPath = activeDocumentInfo.documentPath?.trim();

  if (!expectedDocumentName && !expectedDocumentPath) {
    return undefined;
  }

  return {
    ...(expectedDocumentName ? { expectedDocumentName } : {}),
    ...(expectedDocumentPath ? { expectedDocumentPath } : {}),
  };
}

export function projectChangedResult(): ParameterAdditionResult {
  return {
    success: false,
    addedCount: 0,
    error: 'The active Revit project changed while parameter setup was running. Recheck this step before continuing.',
  };
}

export async function runCoalescedParameterAddition(
  inFlightRef: { current: Promise<ParameterAdditionResult> | null },
  run: () => Promise<ParameterAdditionResult>,
): Promise<ParameterAdditionResult> {
  if (inFlightRef.current) {
    return inFlightRef.current;
  }

  const operationPromise = run();
  inFlightRef.current = operationPromise;

  try {
    return await operationPromise;
  } finally {
    if (inFlightRef.current === operationPromise) {
      inFlightRef.current = null;
    }
  }
}
